import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, Button, Tooltip } from "@ann/web-components";
import { UsersContainer, NameCell, RoleTextTruncated } from "./Users.styled";
import { UserTableSection } from "./components";
import { getUserList, sendEnrollmentInvitation } from "../../../../services";
import { formatLastLogin } from "../../../../utils";
import { useDebounce } from "../../../../../../../hooks";
import { useToast } from "../../../../../../../components/toast";

// TODO: Set to true when Groups feature is ready
const SHOW_GROUPS_COLUMN = false;
// TODO: Enable when API returns user type (e.g. internal vs external)
const SHOW_USER_TYPE_COLUMN = false;
// TODO: Enable when API returns MFA status
const SHOW_MFA_COLUMN = false;

/** Show tooltip with full role list when string is longer than this (chars). */
const ROLE_TOOLTIP_MIN_LENGTH = 36;

const ENABLE_USER_DETAIL_NAVIGATION = true;

const Users = ({ selectedCellsCount, onSelectionChange, breadcrumbHelpers }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [sendingInvitationEmail, setSendingInvitationEmail] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderDirection, setOrderDirection] = useState("ASC");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
    perPage: 0,
  });
  // Filter states
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedRoleUuids, setSelectedRoleUuids] = useState([]);
  const [selectedGroupUuids, setSelectedGroupUuids] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedUserUuids, setSelectedUserUuids] = useState([]);
  
  // Debounce search keyword
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

  useEffect(() => {
    breadcrumbHelpers?.setPageBreadcrumb?.("Users", null, { pageHref: "/v2/user-management" });
  }, [breadcrumbHelpers]);

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUserList({
        page: currentPage,
        page_size: itemsPerPage,
        order_by: orderBy,
        order_direction: orderDirection,
        include_deleted: false,
        search: debouncedSearchKeyword,
        roleUuids: selectedRoleUuids,
        groupUuids: selectedGroupUuids,
        statuses: selectedStatuses,
      });
      

      if (response && response.body) {
        setUsers(response.body);
        if (response.meta) {
          // Meta already comes in camelCase from backend
          setMeta({
            totalItems: response.meta.totalItems || 0,
            totalPages: response.meta.totalPages || 0,
            currentPage: response.meta.currentPage || currentPage,
            perPage: response.meta.perPage || itemsPerPage,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, orderBy, orderDirection, debouncedSearchKeyword, selectedRoleUuids, selectedGroupUuids, selectedStatuses]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSelectionChange = (cells) => {
    // Extract userUuids from selected cells
    const userUuids = cells.map((cell) => cell.id).filter(Boolean);
    setSelectedUserUuids(userUuids);
    
    if (onSelectionChange) {
      onSelectionChange(cells.length);
    }
  };

  const handleNameClick = (userId, userName) => {
    navigate(`/v2/user-management/users/${userId}`, {
      state: { userName }
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSortChange = (selectedOption) => {
    // SortMenu passes camelCase (createdAt, fullName, etc.), but we store snake_case internally
    const camelToSnakeMap = {
      createdAt: "created_at",
      fullName: "full_name",
      updatedAt: "updated_at",
      email: "email",
      status: "status",
    };
    const validSnakeCase = ["created_at", "email", "full_name", "status", "updated_at"];
    const mappedOrderBy = camelToSnakeMap[selectedOption] || "created_at";
    const newOrderBy = validSnakeCase.includes(mappedOrderBy) ? mappedOrderBy : "created_at";
    setOrderBy(newOrderBy);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handleOrderChange = (currentOrder) => {
    // Toggle order: asc -> desc, desc -> asc
    const newOrder = currentOrder === 'asc' ? 'DESC' : 'ASC';
    setOrderDirection(newOrder);
    setCurrentPage(1); // Reset to first page when order changes
  };

  const handleSearchChange = (keyword) => {
    setSearchKeyword(keyword);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const handleRoleChange = (roleUuid) => {
    setSelectedRoleUuids(roleUuid ? [roleUuid] : []);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleGroupChange = (groupUuid) => {
    setSelectedGroupUuids(groupUuid ? [groupUuid] : []);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleStatusChange = (status) => {
    setSelectedStatuses(status ? [status] : []);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleInviteUsers = () => {
    navigate("/v2/user-management/invite-user");
  };

  const handleClearSelection = () => {
    setSelectedUserUuids([]);
    if (onSelectionChange) {
      onSelectionChange(0);
    }
  };

  // Función para recargar usuarios después de mutaciones (desactivar, asignar roles, etc.)
  const refetchUsers = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  /** Backend allows resend for pending enrollment; inactive users can be invited again. */
  const canSendInvitationForStatus = (status) => {
    if (typeof status !== "string") return false;
    const u = status.toUpperCase();
    return u === "IN_PROGRESS" || u === "INACTIVE";
  };

  const formatAccountStatusDisplay = (status) => {
    if (!status) return "—";
    const u = status.toUpperCase();
    if (u === "IN_PROGRESS") return "In progress";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const handleSendInvitation = useCallback(
    async (email) => {
      if (!email?.trim()) return;
      setSendingInvitationEmail(email.trim());
      try {
        await sendEnrollmentInvitation(email.trim());
        toast.success({
          message: "Invitation sent.",
          canClose: true,
        });
        refetchUsers();
      } catch (err) {
        toast.error({
          message: err?.message || "Failed to send invitation.",
          canClose: true,
        });
      } finally {
        setSendingInvitationEmail(null);
      }
    },
    [toast, refetchUsers]
  );

  const getAccountStatusColor = (status) => {
    // Normalize status to handle both API format (ACTIVE) and display format (Active)
    const normalizedStatus = typeof status === 'string' ? status.toUpperCase() : '';
    
    switch (normalizedStatus) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING':
        return 'alert';
      case 'INACTIVE':
        return 'neutral';
      case 'DELETED':
        return 'error';
      case 'EXPIRED':
        return 'error';
      case 'IN_PROGRESS':
        return 'alert';
      default:
        return 'neutral';
    }
  };

  // Map API response to table data
  const tableData = users.map((user) => {
    // Programación defensiva: soportar ambos formatos (uuid y userUuid)
    const userId = user.uuid || user.userUuid || user.id || user.user_uuid;
    
    // Map status from API (e.g., "active") to display format (e.g., "Active")
    // Programación defensiva: status puede ser null en el nuevo formato
    const emailTrimmed = (user.email || "").trim();
    const statusDisplay = user.status ? formatAccountStatusDisplay(user.status) : "—";
    
    const rolesJoined =
      user.roles && Array.isArray(user.roles) && user.roles.length > 0
        ? user.roles.map((role) => role.roleName || role.roleCode).filter(Boolean).join(", ")
        : "";

    const rolesDisplay = (() => {
      if (!rolesJoined) return "—";
      const roleInner = <RoleTextTruncated>{rolesJoined}</RoleTextTruncated>;
      if (rolesJoined.length >= ROLE_TOOLTIP_MIN_LENGTH) {
        return (
          <Tooltip content={rolesJoined} position="top" arrow={false}>
            {roleInner}
          </Tooltip>
        );
      }
      return rolesJoined;
    })();

    const row = {
      id: userId,
      name: ENABLE_USER_DETAIL_NAVIGATION ? (
        <NameCell onClick={() => handleNameClick(userId, user.fullName || user.full_name)}>
          {user.fullName || user.full_name || "—"}
        </NameCell>
      ) : (
        user.fullName || user.full_name || "—"
      ),
      email: user.email || "—",
      accountStatus: (
        <Tag
          color={getAccountStatusColor(user.status)}
          size="small"
          text={statusDisplay}
        />
      ),
      lastLogin: (user.updatedAt || user.updated_at)
        ? formatLastLogin(user.updatedAt || user.updated_at)
        : "—",
      role: rolesDisplay,
      invitationAction: (
        <Button
          disabled={
            !canSendInvitationForStatus(user.status) ||
            !emailTrimmed ||
            sendingInvitationEmail === emailTrimmed
          }
          label="Send invitation"
          onClick={() => handleSendInvitation(user.email)}
          pill
          size="small"
          type="secondary"
        />
      ),
    };

    if (SHOW_GROUPS_COLUMN) {
      row.groups = (
        <Button
          iconLeft="plus"
          label="Add Group"
          pill
          size="small"
          type="secondary"
        />
      );
    }
    if (SHOW_USER_TYPE_COLUMN) {
      row.type = "Internal"; // Map from API when enabled
    }
    if (SHOW_MFA_COLUMN) {
      row.mfa = "—"; // Map from API when enabled
    }

    return row;
  });

  const headers = [
    { label: "Name", value: "name" },
    { label: "Email", value: "email" },
    { label: "Account Status", value: "accountStatus" },
    { label: "Last Login", value: "lastLogin" },
    { label: "Role", value: "role" },
    { label: "Invitation", value: "invitationAction" },
    ...(SHOW_GROUPS_COLUMN ? [{ label: "Groups", value: "groups" }] : []),
    ...(SHOW_USER_TYPE_COLUMN ? [{ label: "Type", value: "type" }] : []),
    ...(SHOW_MFA_COLUMN ? [{ label: "MFA", value: "mfa" }] : []),
  ];

  // Determine selected sort option based on orderBy
  // orderBy is in snake_case internally, but SortMenu expects camelCase
  const getSelectedSortOption = () => {
    const snakeToCamelMap = {
      created_at: "createdAt",
      full_name: "fullName",
      updated_at: "updatedAt",
      email: "email",
      status: "status",
    };
    const validSnakeCase = ["created_at", "email", "full_name", "status", "updated_at"];
    const camelCaseValue = snakeToCamelMap[orderBy] || "createdAt";
    return validSnakeCase.includes(orderBy) ? camelCaseValue : "createdAt";
  };

  // Determine order direction (convert "DESC" to "desc", "ASC" to "asc")
  const getOrderDirection = () => {
    return orderDirection.toLowerCase();
  };

  // Show empty state only when NOT loading AND there are no users
  const showEmptyState = !loading && tableData.length === 0;

  return (
    <UsersContainer>
      <UserTableSection
        selectedItemsCount={selectedCellsCount}
        selectedUserUuids={selectedUserUuids}
        onClearSelection={handleClearSelection}
        tableData={tableData}
        headers={headers}
        onSelectionChange={handleSelectionChange}
        currentPage={meta.currentPage || currentPage}
        itemsPerPage={meta.perPage || itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onPageChange={handlePageChange}
        totalItems={meta.totalItems || 0}
        totalPages={meta.totalPages || 0}
        loading={loading}
        onSortChange={handleSortChange}
        onOrderChange={handleOrderChange}
        selectedSortOption={getSelectedSortOption()}
        orderDirection={getOrderDirection()}
        onSearchChange={handleSearchChange}
        onRoleChange={handleRoleChange}
        onGroupChange={handleGroupChange}
        onStatusChange={handleStatusChange}
        onRefetchUsers={refetchUsers}
        showEmptyState={showEmptyState}
        onInviteUsers={handleInviteUsers}
      />
    </UsersContainer>
  );
};

export default Users;

